import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Story } from '../types/Story';
import { useNavigate } from 'react-router-dom';
import { Button } from './common/Button';
import { toggleLikeStory } from '../services/storyService';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import defaultCoverImage from '../images/story_cover.jpg';
import { getOptimizedImage } from '../utils/cloudinary';

interface StoryCardProps {
  story: Story;
  compact?: boolean;
  showLike?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  isMaxReached?: boolean;
}

/* ─── Animations ─── */

const popIn = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); opacity: 1; }
`;

const heartBeat = keyframes`
  0% { transform: scale(1); }
  25% { transform: scale(1.3); }
  50% { transform: scale(1); }
  75% { transform: scale(1.15); }
  100% { transform: scale(1); }
`;

/* ─── Card ─── */

const Card = styled.div<{ $compact: boolean; $isSelectable: boolean; $isSelected: boolean; $isMaxReached: boolean }>`
  border-radius: 14px;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.2s ease, opacity 0.2s ease;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: ${props => props.$isSelectable ? (props.$isMaxReached ? 'not-allowed' : 'pointer') : 'default'};
  border: 2px solid ${props => props.$isSelected ? COLORS.PRIMARY : 'transparent'};
  opacity: ${props => props.$isMaxReached ? 0.45 : 1};
  
  &:hover {
    transform: ${props => props.$isMaxReached ? 'none' : 'translateY(-5px)'};
    box-shadow: ${props => props.$isMaxReached 
      ? '0 2px 8px rgba(0, 0, 0, 0.06)' 
      : '0 12px 28px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.04)'};
  }

  &:hover img, &:hover .cover-bg {
    transform: scale(1.05);
  }

  ${props => props.$isSelected && css`
    box-shadow: 0 0 0 3px ${COLORS.PRIMARY}33, 0 4px 12px rgba(0, 0, 0, 0.08);
  `}
`;

/* ─── Selection ─── */

const SelectionCheckbox = styled.div<{ $isSelected: boolean; $isMaxReached: boolean }>`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: ${props => props.$isSelected ? COLORS.PRIMARY : 'rgba(255, 255, 255, 0.92)'};
  border: 2px solid ${props => props.$isSelected ? COLORS.PRIMARY : 'rgba(0, 0, 0, 0.15)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  cursor: ${props => props.$isMaxReached ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$isMaxReached ? 0.4 : 1};
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  
  ${props => props.$isSelected && css`
    animation: ${popIn} 0.3s ease-out;
  `}
  
  &:hover {
    transform: ${props => props.$isMaxReached ? 'none' : 'scale(1.12)'};
    box-shadow: ${props => props.$isMaxReached ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.15)'};
  }

  svg {
    width: 14px;
    height: 14px;
    color: white;
    opacity: ${props => props.$isSelected ? 1 : 0};
    transition: opacity 0.15s ease;
  }
`;

const SelectionNumber = styled.span`
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
`;

/* ─── Cover Image ─── */

const ImageWrapper = styled.div<{ $compact: boolean }>`
  height: ${props => (props.$compact ? '150px' : '200px')};
  position: relative;
  overflow: hidden;
`;

const CoverBg = styled.div<{ $imageUrl?: string }>`
  width: 100%;
  height: 100%;
  background-color: ${props => props.$imageUrl ? 'transparent' : '#e9ecef'};
  background-image: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  transition: transform 0.35s ease;
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.35) 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
`;

const BadgeRow = styled.div`
  position: absolute;
  top: 10px;
  left: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  z-index: 2;
  pointer-events: none;
  
  & > * {
    pointer-events: auto;
  }
`;

const Badge = styled.span<{ $variant?: 'type' | 'age' }>`
  font-size: 0.7rem;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 6px;
  letter-spacing: 0.02em;
  backdrop-filter: blur(6px);
  
  ${props => props.$variant === 'type' && css`
    background-color: ${COLORS.PRIMARY}dd;
    color: white;
  `}
  
  ${props => props.$variant === 'age' && css`
    background-color: rgba(0, 0, 0, 0.55);
    color: white;
  `}
`;

/* ─── Card Content ─── */

const CardContent = styled.div<{ $compact: boolean }>`
  padding: ${props => (props.$compact ? '12px' : '14px 16px 16px')};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled.h3<{ $compact: boolean }>`
  font-size: ${props => (props.$compact ? '0.95rem' : '1.05rem')};
  font-weight: 600;
  margin: 0 0 6px 0;
  color: #1a1a2e;
  line-height: 1.35;
  letter-spacing: -0.01em;
  
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Summary = styled.p<{ $compact: boolean }>`
  font-size: ${props => (props.$compact ? '0.8rem' : '0.85rem')};
  color: #6c757d;
  margin: 0 0 ${props => (props.$compact ? '10px' : '14px')} 0;
  flex: 1;
  line-height: 1.5;
  
  display: -webkit-box;
  -webkit-line-clamp: ${props => (props.$compact ? 2 : 3)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ─── Footer / Meta ─── */

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MetaGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #adb5bd;
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const ReadingTime = styled(MetaItem)`
  color: #868e96;
  
  svg {
    color: #868e96;
  }
`;

/* ─── Action Buttons ─── */

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f3f5;
`;

const LikeButtonStyled = styled.button<{ $liked: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid ${props => props.$liked ? '#ffe0e0' : '#e9ecef'};
  background: ${props => props.$liked ? '#fff5f5' : '#f8f9fa'};
  color: ${props => props.$liked ? '#e03131' : '#868e96'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  svg {
    width: 15px;
    height: 15px;
    transition: transform 0.2s ease;
  }
  
  &:hover {
    background: ${props => props.$liked ? '#ffe0e0' : '#f1f3f5'};
    border-color: ${props => props.$liked ? '#fcc' : '#dee2e6'};
    
    svg {
      transform: scale(1.15);
    }
  }
  
  ${props => props.$liked && css`
    svg {
      animation: ${heartBeat} 0.4s ease;
    }
  `}
`;

const ReadButtonStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 500;
  border-radius: 8px;
  border: none;
  background: ${COLORS.PRIMARY};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  &:hover {
    background: ${COLORS.PRIMARY_DARK};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px ${COLORS.PRIMARY}44;
  }
`;

/* ─── Component ─── */

export const StoryCard = ({ 
  story, 
  compact = false, 
  showLike = true,
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
  isMaxReached = false
} : StoryCardProps) => {
  const navigate = useNavigate();
  const [likes, setLikes] = useState<number>(story.likes);
  const [isLiked, setIsLiked] = useState<boolean>(story.liked);
  const { user } = useAuth();
  const { t } = useTranslation();

  const coverImage = story.imageUrls?.[0] 
    ? getOptimizedImage(story.imageUrls[0], 400) 
    : defaultCoverImage;
  
  const handleReadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isSelectable) {
      navigate(`/story/${story.id}/read`);
    }
  };
  
  const handleCardClick = () => {
    if (isSelectable && onToggleSelect && !isMaxReached) {
      onToggleSelect();
    } else if (!isSelectable) {
      navigate(`/story/${story.id}`);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelect && !isMaxReached) {
      onToggleSelect();
    }
  };

  const handleLikeClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isSelectable) return;
    if (!user?.id) {
      console.warn('User not logged in.');
      return;
    }

    try {
      const response = await toggleLikeStory(story.id, user.id);

      if (response.success && response.data) {
        setLikes(response.data.likes);
        setIsLiked(response.data.liked);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const readingTimeMinutes = Math.max(1, Math.ceil(story.content.split(/\s+/).length / 200));
  
  return (
    <Card 
      $compact={compact} 
      $isSelectable={isSelectable}
      $isSelected={isSelected}
      $isMaxReached={isMaxReached}
      onClick={handleCardClick}
    >
      <ImageWrapper $compact={compact}>
        <CoverBg className="cover-bg" $imageUrl={coverImage} />
        <ImageOverlay />
        
        <BadgeRow>
          {isSelectable ? (
            <SelectionCheckbox 
              $isSelected={isSelected}
              $isMaxReached={isMaxReached}
              onClick={handleCheckboxClick}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="20 6 9 17 4 12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </SelectionCheckbox>
          ) : (
            <Badge $variant="type">
              {t(`storyTypes.${story.storyType}`)}
            </Badge>
          )}
          
          <Badge $variant="age">
            {t(`ageRanges.${story.ageRange}`)}
          </Badge>
        </BadgeRow>
      </ImageWrapper>
      
      <CardContent $compact={compact}>
        <Title $compact={compact}>{story.title}</Title>
        <Summary $compact={compact}>
          {story.summary || story.content.substring(0, 120) + '...'}
        </Summary>
        
        <MetaGroup>
          {readingTimeMinutes && (
            <ReadingTime>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              {readingTimeMinutes} min
            </ReadingTime>
          )}
          <MetaItem>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            {story.readCount}
          </MetaItem>
        </MetaGroup>

        {!isSelectable && (
          <ActionRow>
            {showLike && (
              <LikeButtonStyled $liked={isLiked} onClick={handleLikeClick}>
                <svg viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                {likes}
              </LikeButtonStyled>
            )}
            
            <ReadButtonStyled onClick={handleReadClick}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
              {t('storyCard.readStory')}
            </ReadButtonStyled>
          </ActionRow>
        )}
      </CardContent>
    </Card>
  );
};